import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { clearViewCaches } from "../data/view-cache";
import { authApi, setCsrfToken, useDevelopmentFixtures, type SessionResponse } from "../api/client";
import type { Permission, SessionUser } from "../data/types";

type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: AuthStatus;
  user: SessionUser | null;
  login: (email: string, password: string, rememberDevice: boolean) => Promise<void>;
  acceptInvitation: (token: string, name: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function getDevelopmentUser(): Promise<SessionUser> {
  const { fixtureUser } = await import("../dev/fixtures");
  await new Promise((resolve) => window.setTimeout(resolve, 220));
  return fixtureUser;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const sessionGeneration = useRef(0);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);

  useEffect(() => {
    let active = true;
    const generation = sessionGeneration.current;
    const loadSession = async () => {
      try {
        if (useDevelopmentFixtures) {
          const developmentUser = await getDevelopmentUser();
          if (!active || generation !== sessionGeneration.current) return;
          setUser(developmentUser);
          setStatus("authenticated");
          return;
        }
        const response = await authApi.session();
        if (!active || generation !== sessionGeneration.current) return;
        if (response.csrfToken) setCsrfToken(response.csrfToken);
        setUser(response.user ?? null);
        setStatus(response.authenticated && response.user ? "authenticated" : "anonymous");
      } catch {
        if (!active || generation !== sessionGeneration.current) return;
        setUser(null);
        setStatus("anonymous");
      }
    };
    void loadSession();
    return () => {
      active = false;
    };
  }, []);

  const establishSession = useCallback((response: SessionResponse) => {
    if (!response.authenticated || !response.user) throw new Error("No fue posible iniciar sesión.");
    sessionGeneration.current += 1;
    if (response.csrfToken) setCsrfToken(response.csrfToken);
    setUser(response.user);
    setStatus("authenticated");
  }, []);

  const login = useCallback(async (email: string, password: string, rememberDevice: boolean) => {
    if (useDevelopmentFixtures) {
      if (!email || password.length < 12) throw new Error("Correo o contraseña incorrectos.");
      establishSession({ authenticated: true, user: await getDevelopmentUser() });
      return;
    }
    establishSession(await authApi.login(email, password, rememberDevice));
  }, [establishSession]);

  const acceptInvitation = useCallback(async (token: string, name: string, password: string) => {
    if (useDevelopmentFixtures) {
      if (!name.trim() || password.length < 12) throw new Error("Revisa el nombre y la contraseña.");
      establishSession({ authenticated: true, user: await getDevelopmentUser() });
      return;
    }
    establishSession(await authApi.acceptInvitation(token, name, password));
  }, [establishSession]);

  const logout = useCallback(async () => {
    sessionGeneration.current += 1;
    try {
      if (!useDevelopmentFixtures) await authApi.logout();
    } finally {
      setCsrfToken("");
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  // Cached screens belong to the person who loaded them.
  useEffect(() => clearViewCaches(), [user?.id]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      login,
      acceptInvitation,
      logout,
      hasPermission: (permission) => Boolean(user?.permissions.includes("*") || user?.permissions.includes(permission)),
    }),
    [acceptInvitation, login, logout, status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth debe utilizarse dentro de AuthProvider.");
  return value;
}
